import React from 'react';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Connection } from '../../../../common/hooks/use-home-api.hook';
import { CONNECTION_ICONS } from './Connections';

/**
 * What the team can see, tucked under the composer.
 *
 * One glyph per connection, solid when it is connected and faint when it is
 * not, so the vendor sees at the moment of asking what their team can work
 * with. "Connect N more" opens the full list. It sits under the composer rather
 * than as a section of its own because that is where it matters: a question
 * about revenue asked with no shop connected is the question to warn about.
 */
export const ConnectionsStrip: React.FC<{
    connections: Connection[] | null;
    onOpen: () => void;
}> = ({ connections, onOpen }) => {
    const { t } = useTranslation();
    const open = (connections ?? []).filter((c) => c.state === 'missing' || c.state === 'attention').length;

    return (
        <div className="relative -mt-3 mx-3 flex items-center justify-between gap-3 rounded-b-xl border border-t-0 bg-muted/50 px-3 pt-5 pb-2">
            <div className="flex min-w-0 items-center gap-2.5">
                {!connections && <Skeleton className="h-4 w-40" />}
                {connections?.map((c) => {
                    const Icon = CONNECTION_ICONS[c.key];
                    const name = t(`AGENTS.CONNECT.ITEM.${c.key.toUpperCase()}.NAME`);
                    const state = c.state === 'connected'
                        ? (c.detail ?? t('AGENTS.CONNECT.STATE.CONNECTED', 'Connected'))
                        : c.state === 'attention'
                            ? t('AGENTS.CONNECT.ACTION.FIX', 'Fix')
                            : c.state === 'locked'
                                ? t('AGENTS.CONNECT.LOCKED', 'Not in your plan')
                                : t(`AGENTS.CONNECT.ITEM.${c.key.toUpperCase()}.HINT`);
                    return (
                        <Tooltip key={c.key}>
                            <TooltipTrigger asChild>
                                <span
                                    tabIndex={0}
                                    aria-label={`${name}: ${state}`}
                                    className="relative inline-flex outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                                >
                                    <Icon
                                        aria-hidden
                                        className={cn(
                                            'size-4',
                                            c.state === 'connected' ? 'text-foreground' : 'text-muted-foreground/35',
                                        )}
                                    />
                                    {c.state === 'attention' && (
                                        <span className="absolute -end-0.5 -top-0.5 size-1.5 rounded-full bg-warning" aria-hidden />
                                    )}
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <span className="font-medium">{name}</span> · {state}
                            </TooltipContent>
                        </Tooltip>
                    );
                })}
            </div>

            <button
                type="button"
                onClick={onOpen}
                className="flex shrink-0 items-center gap-0.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
                {connections && (open
                    ? t('AGENTS.CONNECT.STRIP_MORE', { count: open, defaultValue: 'Connect {{count}} more' })
                    : t('AGENTS.CONNECT.STRIP_ALL', 'Everything is connected'))}
                <ChevronRight className="size-3.5 rtl:rotate-180" aria-hidden />
            </button>
        </div>
    );
};
